import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Flex, List, message, Space, Tag } from 'antd';
import StepsHeading from './StepsHeading';
import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import {
  CustomButton,
  CustomModalWithoutButton,
  SearchInput,
} from '../../../components';
import EllipsisTooltip from '../../../components/EllipsisTooltip';
import ConnectFormModal from '../../Modals/ConnectFormModal';
import { useSpin } from '../../../components/SpinContext';
import { useToken } from '../../../common/helper';
import useUnauthorize from '@/hooks/useUnauthorize';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllCouriers,
  selectCouriersState,
  CourierItem,
} from '../../../redux-toolkit/slice/onboardingSlice';
import { AppDispatch, RootState } from '../../../redux-toolkit/store';
import chevronRight from '../../../assets/images/chevron-right.svg';
import { DeleteCourierAccount } from '../../../apis/onboarding';
import { getProviderImagePath } from '../../../common/utils/imageUtils';
import ZoomlogiMark from '../../../assets/images/zoomlogi-mark.svg';
import Arrows from '../../../assets/images/arrows.png';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import { statusConfig } from '@/components/StatusTag';
import { LinkInlineIcon } from '../../../assets/icons/icons';

interface CourierItemWithMessage extends CourierItem {
  isIntegratedMessage?: boolean;
}

/**
 * Couriers onboarding step component for connecting transport providers.
 * Allows users to link courier accounts for advanced tracking capabilities.
 */
const Couriers: React.FC = () => {
  const [list, setList] = useState<CourierItemWithMessage[]>([]);
  const [filteredData, setFilteredData] = useState<CourierItemWithMessage[]>(
    []
  );
  const [searchText, setSearchText] = useState<string>('');
  const { showSpin, hideSpin } = useSpin();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalName, setModalName] = useState<string>('');
  const { token } = useToken();
  const authToken = token ?? '';
  const { unauthorize } = useUnauthorize();
  const [courier, setCourier] = useState<CourierItem | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((state: RootState) => selectCouriersState(state));
  const [requestStatus, setRequestStatus] = useState<string | number | null>(
    null
  );

  const refreshCouriers = useCallback((): void => {
    void Promise.resolve(dispatch(fetchAllCouriers(authToken))).catch(
      (): undefined => undefined
    );
  }, [dispatch, authToken]);

  useEffect(() => {
    refreshCouriers();
  }, [refreshCouriers]);

  useEffect(() => {
    if (state) {
      setRequestStatus(state?.status ? state.status : null);
      setList(state.courierList);
      setFilteredData(state.courierList);
    }
  }, [state]);

  useEffect(() => {
    if (typeof requestStatus === 'number') {
      unauthorize({ status: requestStatus });
    }
  }, [requestStatus, unauthorize]);

  const handleListMessage = (item: CourierItemWithMessage): void => {
    setFilteredData((prevList) =>
      prevList.map((ele) =>
        ele.id === item.id ? { ...ele, isIntegratedMessage: true } : ele
      )
    );
  };

  const handleDelete = (item: CourierItem): void => {
    if (item && item?.company_courier) {
      showSpin();
      DeleteCourierAccount(item?.company_courier, authToken)
        .then((res) => {
          const data = res?.data || null;
          if (data) {
            if (data.data?.is_success) {
              message.success(data?.data?.message);
              refreshCouriers();
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    }
  };

  const handleButtonClick = (item: CourierItem): void => {
    if (item) {
      setCourier(item);
      setModalName(item?.title ? item.title : '');
    }
    setModalOpen(true);
  };

  const onModalConfirm = (): void => {
    setModalOpen(false);
    refreshCouriers();
    setCourier(null);
    setModalName('');
  };

  const handleSearch = (value: string): void => {
    const searchValue = value.toLowerCase();
    setSearchText(searchValue);

    if (searchValue.trim() === '') {
      setFilteredData(list);
    } else {
      const filteredItems = list.filter((item) =>
        item.title.toLowerCase().includes(searchValue)
      );
      setFilteredData(filteredItems);
    }
  };

  return (
    <>
      <div className='default-steps-content onboarding-transport-step'>
        <StepsHeading
          heading={`Connect your ${themeT('transportProvider', theme.themeType).toLowerCase()}s`}
          text={`Connect your ${themeT('transportProvider', theme.themeType).toLowerCase()}s to track shipments in real time, all in one place. Choose Basic Tracking for broad updates or Advanced Tracking for richer status updates and more accurate ETAs.`}
        />
        <SearchInput
          placeholder={`Search for a ${themeT('transportProvider', theme.themeType).toLowerCase()}...`}
          width='min(420px, 100%)'
          defaultValue={searchText}
          onChange={handleSearch}
          style={{ height: '36px' }}
        />

        <List
          className='custom-list-container'
          itemLayout='horizontal'
          dataSource={filteredData}
          renderItem={(item) => {
            const imagePath = getProviderImagePath(
              item.title,
              'transportProvider'
            );

            return (
              <List.Item className='onboarding-transport-list-item'>
                <div className='onboarding-transport-info'>
                  <Avatar
                    className='onboarding-transport-avatar'
                    src={
                      <img
                        src={imagePath}
                        alt='avatar'
                        className='onboarding-courier-img'
                      />
                    }
                  />

                  <Space className='onboarding-transport-title'>
                    <EllipsisTooltip
                      className='onboarding-transport-name'
                      text={item.title || ''}
                    />

                    {item?.is_enabled
                      ? item?.status === null
                        ? (item?.is_zoom_logi_provider || item?.is_public) && (
                            <Tag
                              color='success'
                              icon={
                                <CheckOutlined className='onboarding-courier-icon-success' />
                              }
                              style={statusConfig.Synced}
                            >
                              Basic Tracking
                            </Tag>
                          )
                        : item?.status !== null
                          ? !item?.is_zoom_logi_provider &&
                            !item?.is_public &&
                            (item?.status === 'pending' ? (
                              <Tag
                                color='default'
                                icon={
                                  <ClockCircleOutlined className='onboarding-courier-icon-muted' />
                                }
                              >
                                Checking Credentials
                              </Tag>
                            ) : item?.status === 'linked' ? (
                              <Tag
                                //color='success'
                                icon={
                                  <CheckOutlined className='onboarding-courier-icon-success' />
                                }
                                style={statusConfig.Synced}
                              >
                                Synced
                              </Tag>
                            ) : item?.status === 'basic' ? (
                              <Tag
                                color='success'
                                icon={
                                  <CheckOutlined className='onboarding-courier-icon-success' />
                                }
                                style={statusConfig.Synced}
                              >
                                Basic Tracking
                              </Tag>
                            ) : item?.status === 'error' ? (
                              <Tag
                                color='error'
                                icon={
                                  <CloseOutlined className='onboarding-courier-icon-danger' />
                                }
                              >
                                Incorrect Access Credentials
                              </Tag>
                            ) : null)
                          : null
                      : null}
                  </Space>
                </div>

                {item.is_enabled ? (
                  item?.status === null ? (
                    !item?.is_zoom_logi_provider && !item.is_public ? (
                      <CustomButton
                        className='onboarding-transport-link-btn'
                        onClick={() => handleButtonClick(item)}
                        height='36px'
                        fontSize='14px'
                        text='Link account'
                        bgColor='var(--link-btn-bg)'
                        borderRadius='8px'
                        color='var(--text-primary)'
                        boxShadow='none'
                        iconPosition='left'
                        icon={<LinkInlineIcon />}
                        gap='10px'
                        fontWeight={500}
                      />
                    ) : item?.is_zoom_logi_provider || item.is_public ? (
                      ''
                    ) : null
                  ) : item.status !== null ? (
                    item.status === 'pending' ? (
                      <div className='onboarding-transport-icon-action'>
                        <CloseOutlined
                          className='onboarding-courier-icon-muted'
                          onClick={() => handleDelete(item)}
                        />
                      </div>
                    ) : item.status === 'basic' ? (
                      <div className='onboarding-transport-icon-action'>
                        <CloseOutlined
                          className='onboarding-courier-icon-muted'
                          onClick={() => handleDelete(item)}
                        />
                      </div>
                    ) : item.status === 'linked' || item.status === 'error' ? (
                      <img
                        className='onboarding-transport-action-icon onboarding-courier-chevron'
                        src={chevronRight}
                        alt=''
                        onClick={() => handleButtonClick(item)}
                      />
                    ) : null
                  ) : null
                ) : (
                  <Flex
                    className='onboarding-transport-action'
                    align='center'
                    justify='center'
                  >
                    {item.isIntegratedMessage ? (
                      <p className='onboarding-courier-message'>
                        A Zoomlogi representative will reach out to you to
                        complete integration
                      </p>
                    ) : (
                      <CustomButton
                        className='onboarding-transport-link-btn'
                        onClick={() => handleListMessage(item)}
                        height='36px'
                        fontSize='14px'
                        text='Link account'
                        bgColor='var(--link-btn-bg)'
                        borderRadius='8px'
                        color='var(--text-primary)'
                        boxShadow='none'
                        iconPosition='left'
                        icon={<LinkInlineIcon />}
                        gap='10px'
                        fontWeight={500}
                      />
                    )}
                  </Flex>
                )}
              </List.Item>
            );
          }}
        />

        {modalOpen && courier && (
          <CustomModalWithoutButton
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            modalTitle={`Connect to ${modalName} for advanced tracking`}
            modalText={`By providing API access to Zoomlogi, you're enabling Zoomlogi to retrieve your shipment information.`}
            modalSize={795}
            modalBorderRadius={'16px'}
            headerIcon={
              <Flex align='center' justify='center' gap={12}>
                <Avatar
                  src={
                    <img
                      src={getProviderImagePath(
                        courier.title,
                        'transportProvider'
                      )}
                      alt={courier.title}
                      className='onboarding-courier-img'
                    />
                  }
                  size={52}
                  className='onboarding-courier-logo-avatar'
                />
                <img
                  src={Arrows}
                  alt='connects to'
                  className='onboarding-courier-arrows'
                />
                <Avatar size={52} className='onboarding-courier-logo-avatar'>
                  <div className='onboarding-courier-mark'>
                    <img
                      src={ZoomlogiMark}
                      alt='Zoomlogi'
                      className='onboarding-courier-img'
                    />
                  </div>
                </Avatar>
              </Flex>
            }
          >
            <ConnectFormModal
              data={courier}
              closeModal={() => setModalOpen(false)}
              onSuccess={onModalConfirm}
            />
          </CustomModalWithoutButton>
        )}
      </div>
    </>
  );
};

export default Couriers;
